import * as Matter from 'matter-js';
import type { BuiltScene, ForceVector } from '../scenes';

const COLOR_GRAVITY = '#1a73e8';
const COLOR_NORMAL = '#34a853';
const COLOR_FRICTION = '#f9ab00';
const COLOR_SPRING = '#9c27b0';
const COLOR_TENSION = '#e91e63';
const COLOR_AIR = '#00bcd4';

const SCALE = 1;
// Contact impulses live in a different unit system from the "mass × gravity" we use for
// gravity vectors. Empirically a multiplier ≈ 4 makes the normal force on a body resting
// under gravity match the gravity vector length, which is the visually correct relationship.
const CONTACT_SCALE = 4;

function gravityForce(body: Matter.Body, engine: Matter.Engine): ForceVector {
  return {
    origin: { ...body.position },
    components: {
      x: engine.gravity.x * body.mass * SCALE,
      y: engine.gravity.y * body.mass * SCALE,
    },
    labelKey: 'force.gravity',
    color: COLOR_GRAVITY,
  };
}

interface MatterContact {
  normalImpulse: number;
  tangentImpulse: number;
}

interface MatterPair {
  bodyA: Matter.Body;
  bodyB: Matter.Body;
  collision: { normal: Matter.Vector; tangent: Matter.Vector };
  contacts: MatterContact[];
}

interface MatterPairs {
  list: MatterPair[];
}

function contactForces(body: Matter.Body, engine: Matter.Engine): ForceVector[] {
  const result: ForceVector[] = [];
  let nx = 0;
  let ny = 0;
  let fx = 0;
  let fy = 0;
  const pairs = (engine.pairs as unknown as MatterPairs).list;
  for (const pair of pairs) {
    if (pair.bodyA !== body && pair.bodyB !== body) continue;
    // Matter's pair.collision.normal points from bodyA into bodyB during penetration; the
    // reaction impulse ON bodyA is therefore in the OPPOSITE direction (push A away from B).
    // For bodyB, the impulse is in the +normal direction.
    const sign = pair.bodyA === body ? -1 : 1;
    const normal = pair.collision.normal;
    const tangent = pair.collision.tangent;
    for (const contact of pair.contacts) {
      nx += sign * normal.x * contact.normalImpulse;
      ny += sign * normal.y * contact.normalImpulse;
      fx += sign * tangent.x * contact.tangentImpulse;
      fy += sign * tangent.y * contact.tangentImpulse;
    }
  }
  if (Math.abs(nx) > 1e-4 || Math.abs(ny) > 1e-4) {
    result.push({
      origin: { ...body.position },
      components: { x: nx * SCALE * CONTACT_SCALE, y: ny * SCALE * CONTACT_SCALE },
      labelKey: 'force.normal',
      color: COLOR_NORMAL,
    });
  }
  if (Math.abs(fx) > 1e-4 || Math.abs(fy) > 1e-4) {
    result.push({
      origin: { ...body.position },
      components: { x: fx * SCALE * CONTACT_SCALE, y: fy * SCALE * CONTACT_SCALE },
      labelKey: 'force.friction',
      color: COLOR_FRICTION,
    });
  }
  return result;
}

// Classify a constraint as 'spring' or 'tension' for display purposes.
// Order of precedence:
//   1. constraint.label === 'spring' | 'tension' (explicit hint set by the scene)
//   2. stiffness < 0.5 → spring (acts elastically), else → tension (mostly rigid rope/rod)
function classifyConstraint(c: Matter.Constraint): 'spring' | 'tension' {
  const label = c.label;
  if (label === 'spring' || label === 'tension') return label;
  const stiffness = c.stiffness ?? 1;
  return stiffness < 0.5 ? 'spring' : 'tension';
}

function constraintForces(body: Matter.Body, scene: BuiltScene): ForceVector[] {
  const result: ForceVector[] = [];
  for (const c of scene.constraints) {
    const other = c.bodyA === body ? c.bodyB : c.bodyB === body ? c.bodyA : null;
    if (!other) continue;
    const dx = other.position.x - body.position.x;
    const dy = other.position.y - body.position.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 1e-6) continue;
    const ux = dx / dist;
    const uy = dy / dist;
    const delta = dist - (c.length ?? 0);
    const stiffness = c.stiffness ?? 1;
    const magnitude = stiffness * delta;
    // For springs/visibly elastic constraints, the displacement-based force is the right
    // visualization. For rigid ropes/rods (stiffness ≥ 0.5), delta hovers near zero because
    // the solver keeps them at rest length — the scene should publish the implied tension
    // via `appliedForces`. We still draw whatever residual displacement force we get, so the
    // user sees the constraint "trying" to do its job, but the magnitude will be small.
    if (Math.abs(magnitude) < 1e-4) continue;
    const kind = classifyConstraint(c);
    result.push({
      origin: { ...body.position },
      components: { x: ux * magnitude * SCALE, y: uy * magnitude * SCALE },
      labelKey: kind === 'spring' ? 'force.spring' : 'force.tension',
      color: kind === 'spring' ? COLOR_SPRING : COLOR_TENSION,
    });
  }
  return result;
}

// Air drag — bodies with `frictionAir > 0` lose velocity proportional to their speed each step.
// Matter's actual formula multiplies velocity by `(1 - frictionAir * timeScale²)` per update;
// the effective force is roughly `-mass · frictionAir · velocity · 60` (velocity is in
// pixels-per-step, scaled here to per-second-equivalent for display).
function airDragForce(body: Matter.Body): ForceVector | null {
  if (body.frictionAir <= 0) return null;
  const speed = Math.hypot(body.velocity.x, body.velocity.y);
  if (speed < 0.05) return null;
  const k = body.frictionAir * body.mass * 60;
  return {
    origin: { ...body.position },
    components: { x: -body.velocity.x * k, y: -body.velocity.y * k },
    labelKey: 'force.air',
    color: COLOR_AIR,
  };
}

export function computeForcesOn(
  body: Matter.Body,
  engine: Matter.Engine,
  scene: BuiltScene,
): ForceVector[] {
  const result: ForceVector[] = [];
  result.push(gravityForce(body, engine));
  result.push(...contactForces(body, engine));
  result.push(...constraintForces(body, scene));
  const drag = airDragForce(body);
  if (drag) result.push(drag);
  if (scene.appliedForces) result.push(...scene.appliedForces(body));
  return result;
}
